import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { getComments } from "~/actions/comments";
import { getPatientIdWithError } from "~/actions/get_patient_id";
import { featureFlags } from "~/common/feature-flags";
import { getLastCorrection } from "~/common/patient";
import { formatPersonName } from "~/components/common/PersonName";
import { selectUser } from "~/slices/user";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";

import { doctorViewerApi } from "./api";
import { ViewerCard } from "./viewer-card";

export function PatientViewer({
  patient,
  inline,
  legacyActions,
  legacyArchiveActions,
  legacyPlanDate,
}: { patient: TPatient } & Pick<React.ComponentProps<typeof ViewerCard>, "inline" | "legacyActions" | "legacyArchiveActions" | "legacyPlanDate">) {
  const location = useLocation();
  const selectedPlan = Number(new URLSearchParams(location.search).get("viewer_plan"));
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const refreshPatient = useCallback(async () => {
    const [, commentsUpdated] = await Promise.all([dispatch(getPatientIdWithError(patient.patient_id)), dispatch(getComments(patient.patient_id))]);
    if (!commentsUpdated) throw new Error("Could not refresh plan comments.");
  }, [dispatch, patient.patient_id]);
  if (!featureFlags.alignv) return null;
  const doctorName =
    user.first_name?.trim() || user.last_name?.trim()
      ? formatPersonName({ person: { first_name: user.first_name ?? "", last_name: user.last_name ?? "", middle_name: user.middle_name }, user, isDoctor: true, useMiddleName: true })
      : "";
  return (
    <ViewerCard
      key={patient.patient_id}
      patientId={patient.patient_id}
      doctorName={doctorName}
      clinicTitle={patient.clinic.title ?? ""}
      api={doctorViewerApi}
      onRefreshPatient={refreshPatient}
      initialPlanId={selectedPlan > 0 ? selectedPlan : undefined}
      inline={inline}
      legacyActions={legacyActions}
      legacyArchiveActions={legacyArchiveActions}
      legacyPlanDate={legacyPlanDate}
      expectedPlanDate={getLastCorrection(patient).plan_date}
    />
  );
}
